import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ComponentSync, LL_H_C, LL_V_L, ModuleFE_Toaster, TS_Link } from '@nu-art/thunderstorm-frontend';
import { filterInstances } from '@nu-art/ts-common';
import { ModuleFE_FloatingWindows, windowRect } from '@nu-art/floating-windows';
import { ICONS, Preview } from '@app/styles';
import './Preview_Variable.scss';
import { resolveIcdCode, resolveLoincLink, resolveRxNav, resolveSnomedLink, valueId_IND, valueID_PosNeg, valueID_YesNo, valueType_Enumerated, valueType_Range, valueType_StandardRange, valueType_Time, valueType_Tuple, } from '@app/hcs-shared';
import { ModuleFE_Variable } from '../ModuleFE_Variable.js';
import { ModuleFE_Value } from '../../value/index.js';
import { ModuleFE_NLPData } from '../../nlp-data/index.js';
import { ModuleFE_MeasurementUnit } from '../../measurement-unit/index.js';
import { ModuleFE_Reference } from '../../reference/index.js';
import { ModuleFE_Tag } from '../../tag/index.js';
export class Preview_Variable extends ComponentSync {
    static show = (variableId) => {
        const variable = ModuleFE_Variable.cache.unique(variableId);
        if (!variable)
            return ModuleFE_Toaster.toastError(`Could not open preview for variable, no variable with id ${variableId} found`);
        const model = {
            key: `preview-${variableId}`,
            className: 'preview-window',
            rect: { ...windowRect.thirdScreen_Right(), height: undefined },
            moveable: true,
            resizable: true,
            content: (cb) => _jsx(Preview_Variable, { variable: variable, closePreview: cb })
        };
        ModuleFE_FloatingWindows.window.add(model);
    };
    // ######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        state.variable = nextProps.variable;
        state.parentValue ??= ModuleFE_Value.cache.unique(state.variable.valueId);
        state.nlpData ??= ModuleFE_NLPData.cache.unique(state.variable.nlpDataId);
        // eslint-disable-next-line no-unsafe-optional-chaining
        if (state.parentValue && 'unitsId' in state.parentValue?.data)
            state.measurementUnit = ModuleFE_MeasurementUnit.cache.unique(state.parentValue.data.unitsId);
        return state;
    }
    // ######################### Logic #########################
    getRefsOfType = (refType) => {
        return filterInstances(this.state.variable.referenceIds.map(refId => {
            const ref = ModuleFE_Reference.cache.unique(refId);
            return ref?.type === refType ? ref : undefined;
        }));
    };
    getValueType = () => {
        const value = this.state.parentValue;
        switch (value?.type) {
            case valueType_Tuple:
                if (value._id === valueID_YesNo)
                    return 'Yes / No';
                if (value._id === valueID_PosNeg)
                    return 'Pos / Neg';
                if (value._id === valueId_IND)
                    return 'Inc / Nor / Dec';
                return 'Tuple';
            case valueType_Enumerated:
                return 'Enumerated';
            case valueType_Range:
                return 'Range';
            case valueType_Time:
                return 'Time';
            case valueType_StandardRange:
                return 'Standard Range';
            default:
                return value?.type;
        }
    };
    // ######################### Render #########################
    render() {
        return _jsxs(Preview.Root, { className: 'preview__variable', children: [this.render_Header(), this.render_Main()] });
    }
    render_Header = () => {
        return _jsxs(Preview.Header, { children: [_jsx(Preview.HeaderTitle, { children: this.state.variable.name }), _jsx(Preview.HeaderTail, { children: _jsx(ICONS.advisor_avn_close.component, { onClick: this.props.closePreview }) })] });
    };
    render_Main = () => {
        return _jsxs(Preview.Main, { children: [_jsxs(Preview.MainColumn, { children: [this.render_Main_NLP(), this.render_Main_Tags(), this.render_Main_Refs()] }), _jsx(Preview.MainColumn, { className: 'basic-info', children: this.render_Main_BaseInfo() })] });
    };
    // ######################### Render - Main Content #########################
    render_Main_BaseInfo = () => {
        const variable = this.state.variable;
        const valueType = this.getValueType();
        const measurementUnit = this.state.measurementUnit?.unitAsString;
        return _jsxs(Preview.SectionVertical, { children: [_jsx(Preview.FieldVertical, { label: 'Type', children: _jsx(Preview.FieldValue, { children: variable.type }) }), _jsx(Preview.FieldVertical, { label: 'Perspective', children: _jsx(Preview.FieldValue, { children: variable.perspective }) }), valueType && _jsx(Preview.FieldVertical, { label: 'Value Type', children: _jsx(Preview.FieldValue, { children: valueType }) }), measurementUnit && _jsx(Preview.FieldVertical, { label: 'Measurement Unit', children: _jsx(Preview.FieldValue, { children: measurementUnit }) })] });
    };
    render_Main_Tags = () => {
        const groups = this.state.variable.tagIds;
        if (!groups.length)
            return;
        return _jsx(Preview.SectionVertical, { children: groups.map(tagGroup => {
                const groupParentTag = ModuleFE_Tag.cache.unique(tagGroup.key);
                return _jsx(Preview.FieldVertical, { label: groupParentTag?.label ?? 'N/A', children: _jsx(LL_H_C, { className: 'tag-list', children: tagGroup.value.map(tagId => {
                            const tag = ModuleFE_Tag.cache.unique(tagId);
                            return _jsx(Preview.FieldValue, { className: 'tag', children: tag?.label ?? 'N/A' }, tagId);
                        }) }) }, tagGroup.key);
            }) });
    };
    // ######################### Render - Main Content - NLP #########################
    render_Main_NLP = () => {
        if (!this.state.nlpData)
            return;
        return _jsx(Preview.SectionVertical, { children: this.render_NLP_Synonyms() });
    };
    render_NLP_Synonyms = () => {
        const synonyms = this.state.nlpData?.synonyms;
        if (!synonyms?.length)
            return;
        return _jsx(Preview.FieldVertical, { label: 'Synonyms', children: _jsx(LL_H_C, { className: 'tag-list', children: synonyms.map(syn => _jsx(Preview.FieldValue, { className: 'tag', children: syn }, syn)) }) });
    };
    // ######################### Render - Main Content - References #########################
    render_Main_Refs = () => {
        return _jsxs(_Fragment, { children: [this.render_Refs_Snomed(), this.render_Refs_Loinc(), this.render_Refs_ICD(), this.render_Refs_ATC(), this.render_Refs_RXCUI()] });
    };
    render_RefLink = (url) => {
        return _jsx(TS_Link, { url: url, target: '_blank', children: _jsx(ICONS.information.component, {}) });
    };
    render_Refs_Snomed = () => {
        const refs = this.getRefsOfType('snomed');
        if (!refs.length)
            return;
        return _jsx(Preview.SectionHorizontal, { children: refs.map(ref => {
                return _jsxs(LL_V_L, { className: 'ref-preview', children: [_jsx(Preview.FieldVertical, { label: 'Snomed ID', children: _jsx(Preview.FieldValue, { children: ref.data.id }) }), _jsx(Preview.FieldVertical, { label: 'Snomed Names', children: _jsx(Preview.FieldValue, { children: ref.data.names?.join(', ') ?? 'N/A' }) }), _jsx(Preview.FieldVertical, { label: 'Snomed ICD10', children: _jsx(Preview.FieldValue, { children: ref.data.icd10?.trim() ?? 'Not In Scope' }) }), this.render_RefLink(resolveSnomedLink(ref.data.id))] }, ref._id);
            }) });
    };
    render_Refs_Loinc = () => {
        const refs = this.getRefsOfType('loinc');
        if (!refs.length)
            return;
        return _jsx(Preview.SectionHorizontal, { children: refs.map(ref => {
                return _jsxs(LL_V_L, { className: 'ref-preview', children: [_jsx(Preview.FieldVertical, { label: 'Loinc ID', children: _jsx(Preview.FieldValue, { children: ref.data.id }) }), _jsx(Preview.FieldVertical, { label: 'Loinc Name', children: _jsx(Preview.FieldValue, { children: ref.data.name ?? 'N/A' }) }), this.render_RefLink(resolveLoincLink(ref.data.id))] }, ref._id);
            }) });
    };
    render_Refs_ICD = () => {
        const refs = this.getRefsOfType('icdCode');
        if (!refs.length)
            return;
        return _jsx(Preview.SectionHorizontal, { children: refs.map(ref => {
                return _jsxs(LL_V_L, { className: 'ref-preview', children: [_jsx(Preview.FieldVertical, { label: 'ICD Code', children: _jsx(Preview.FieldValue, { children: ref.data.id }) }), this.render_RefLink(resolveIcdCode(ref.data.id))] }, ref._id);
            }) });
    };
    render_Refs_ATC = () => {
        const refs = this.getRefsOfType('atc');
        if (!refs.length)
            return;
        return _jsx(Preview.SectionHorizontal, { children: refs.map(ref => {
                return _jsxs(LL_V_L, { className: 'ref-preview', children: [_jsx(Preview.FieldVertical, { label: 'ATC Code', children: _jsx(Preview.FieldValue, { children: ref.data.id }) }), this.render_RefLink(resolveRxNav('ATC', ref.data.id))] }, ref._id);
            }) });
    };
    render_Refs_RXCUI = () => {
        const refs = this.getRefsOfType('rxCUI');
        if (!refs.length)
            return;
        return _jsx(Preview.SectionHorizontal, { children: refs.map(ref => {
                return _jsxs(LL_V_L, { className: 'ref-preview', children: [_jsx(Preview.FieldVertical, { label: 'rxCUI Code', children: _jsx(Preview.FieldValue, { children: ref.data.id }) }), this.render_RefLink(resolveRxNav('RXCUI', ref.data.id))] }, ref._id);
            }) });
    };
}
//# sourceMappingURL=Preview_Variable.js.map